import produce from 'immer';
import { debounce } from 'lodash-es';
import fuzzaldrinPlus from 'fuzzaldrin-plus';
import { GlAvatar, GlAvatarLabeled, GlButton, GlCollapsibleListbox, GlIcon, GlFormGroup, GlFormRadio, GlFormRadioGroup } from '../../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import BoardAddNewColumnForm from '../../../../../../app/assets/javascripts/boards/components/board_add_new_column_form.vue?vue3';
import { ListType, createListMutations, listsQuery, BoardType } from 'ee_else_ce/boards/constants';
import { isScopedLabel } from '~/lib/utils/common_utils';
import { DEFAULT_DEBOUNCE_AND_THROTTLE_MS } from '~/lib/utils/constants';
import { __, s__ } from '~/locale';
import { groupOptionsByIterationCadences, groupByIterationCadences, getIterationPeriod } from 'ee/iterations/utils';
import IterationTitle from 'ee/iterations/components/iteration_title.vue';
import boardLabelsQuery from '~/boards/graphql/board_labels.query.graphql';
import groupBoardMilestonesQuery from '~/boards/graphql/group_board_milestones.query.graphql';
import projectBoardMilestonesQuery from '~/boards/graphql/project_board_milestones.query.graphql';
import namespaceWorkItemTypesQuery from '~/work_items/graphql/namespace_work_item_types.query.graphql';
import { setError } from '../../../../../../app/assets/javascripts/boards/graphql/cache_updates.js?vue3';
import { getListByTypeId } from '~/boards/boards_util';
import usersAutocompleteQuery from '~/graphql_shared/queries/users_autocomplete.query.graphql';
import searchIterationQuery from 'ee/work_items/list/graphql/search_iterations.query.graphql';
import { getStatuses } from '../../work_items/utils.js?vue3';
export const listTypeInfo = {
    [ListType.label]: {
        listPropertyName: 'labels',
        loadingPropertyName: 'isLabelsLoading',
        noneSelected: __('Select a label'),
        searchPlaceholder: __('Search labels')
    },
    [ListType.assignee]: {
        listPropertyName: 'assignees',
        loadingPropertyName: 'isAssigneesLoading',
        noneSelected: __('Select an assignee'),
        searchPlaceholder: __('Search assignees')
    },
    [ListType.milestone]: {
        listPropertyName: 'milestones',
        loadingPropertyName: 'isMilestonesLoading',
        noneSelected: __('Select a milestone'),
        searchPlaceholder: __('Search milestones')
    },
    [ListType.iteration]: {
        listPropertyName: 'iterations',
        loadingPropertyName: 'isIterationsLoading',
        noneSelected: __('Select an iteration'),
        searchPlaceholder: __('Search iterations')
    },
    [ListType.status]: {
        listPropertyName: 'statuses',
        loadingPropertyName: 'isStatusesLoading',
        noneSelected: s__('WorkItem|Select a status'),
        searchPlaceholder: s__('WorkItem|Search status')
    }
};
export default {
    name: 'BoardAddNewColumn',
    i18n: {
        value: __('Value'),
        noResults: __('No matching results')
    },
    components: {
        BoardAddNewColumnForm,
        GlAvatar,
        GlAvatarLabeled,
        GlButton,
        GlCollapsibleListbox,
        GlIcon,
        GlFormGroup,
        GlFormRadio,
        GlFormRadioGroup,
        IterationTitle
    },
    inject: [
        'scopedLabelsAvailable',
        'milestoneListsAvailable',
        'assigneeListsAvailable',
        'iterationListsAvailable',
        'boardType',
        'issuableType',
        'fullPath',
        'isEpicBoard',
        'statusListsAvailable'
    ],
    props: {
        boardId: {
            type: String,
            required: true
        },
        listQueryVariables: {
            type: Object,
            required: true
        },
        lists: {
            type: Object,
            required: true
        },
        position: {
            type: Number,
            required: false,
            default: null
        }
    },
    emits: [
        'filter-items',
        'hide',
        'highlight-list',
        'set-add-column-form-visibility'
    ],
    data () {
        return {
            selectedId: null,
            selectedItem: null,
            columnType: ListType.label,
            selectedIdValid: true,
            labels: [],
            milestones: [],
            assignees: [],
            iterations: [],
            searchTerm: '',
            statuses: []
        };
    },
    apollo: {
        labels: {
            query: boardLabelsQuery,
            variables () {
                return {
                    ...this.baseVariables,
                    isGroup: this.boardType === BoardType.group,
                    isProject: this.boardType === BoardType.project
                };
            },
            skip () {
                return this.columnType !== ListType.label;
            },
            update (data) {
                return data[this.boardType].labels.nodes;
            },
            error (error) {
                setError({
                    error,
                    message: s__('Boards|An error occurred while fetching labels. Please try again.')
                });
            }
        },
        milestones: {
            query () {
                if (this.boardType === BoardType.project) {
                    return projectBoardMilestonesQuery;
                }
                return groupBoardMilestonesQuery;
            },
            variables () {
                return this.baseVariables;
            },
            update (data) {
                return data.namespace.milestones.nodes;
            },
            skip () {
                return this.columnType !== ListType.milestone;
            },
            error (error) {
                setError({
                    error,
                    message: s__('Boards|An error occurred while fetching milestones. Please try again.')
                });
            }
        },
        assignees: {
            query () {
                return usersAutocompleteQuery;
            },
            variables () {
                return {
                    fullPath: this.fullPath,
                    search: this.searchTerm,
                    isProject: this.boardType === BoardType.project
                };
            },
            update (data) {
                return data[this.boardType]?.autocompleteUsers;
            },
            skip () {
                return this.columnType !== ListType.assignee;
            },
            error (error) {
                setError({
                    error,
                    message: s__('Boards|An error occurred while fetching users. Please try again.')
                });
            }
        },
        iterations: {
            query: searchIterationQuery,
            variables () {
                return {
                    ...this.baseVariables,
                    search: this.searchTerm,
                    isProject: this.boardType === BoardType.project
                };
            },
            update (data) {
                return data[this.boardType].iterations.nodes;
            },
            skip () {
                return this.columnType !== ListType.iteration;
            },
            error (error) {
                setError({
                    error,
                    message: s__('Boards|An error occurred while fetching iterations. Please try again.')
                });
            }
        },
        statuses: {
            query: namespaceWorkItemTypesQuery,
            variables () {
                return {
                    fullPath: this.fullPath
                };
            },
            update (data) {
                return getStatuses(data.namespace?.workItemTypes?.nodes);
            },
            skip () {
                return this.columnType !== ListType.status;
            },
            error (error) {
                setError({
                    error,
                    message: s__('Boards|An error occurred while fetching statuses. Please try again.')
                });
            }
        }
    },
    computed: {
        isLabelsLoading () {
            return this.$apollo.queries.labels.loading;
        },
        isMilestonesLoading () {
            return this.$apollo.queries.milestones.loading;
        },
        isAssigneesLoading () {
            return this.$apollo.queries.assignees.loading;
        },
        isIterationsLoading () {
            return this.$apollo.queries.iterations.loading;
        },
        isStatusesLoading () {
            return this.$apollo.queries.statuses.loading;
        },
        baseVariables () {
            return {
                fullPath: this.fullPath,
                searchTerm: this.searchTerm
            };
        },
        info () {
            return listTypeInfo[this.columnType] || {};
        },
        iterationCadences () {
            return groupByIterationCadences(this.items);
        },
        filteredStatuses () {
            // frontend fuzzaldrin search
            if (this.searchTerm) {
                return fuzzaldrinPlus.filter(this.items, this.searchTerm, {
                    key: [
                        'text'
                    ]
                });
            }
            return this.items;
        },
        items () {
            return (this[this.info.listPropertyName] || []).map((i)=>({
                    ...i,
                    text: i.title || i.name,
                    value: i.id
                }));
        },
        listboxItems () {
            if (this.iterationTypeSelected) {
                return groupOptionsByIterationCadences(this.items);
            }
            if (this.statusTypeSelected) {
                return this.filteredStatuses;
            }
            return this.items;
        },
        labelTypeSelected () {
            return this.columnType === ListType.label;
        },
        assigneeTypeSelected () {
            return this.columnType === ListType.assignee;
        },
        milestoneTypeSelected () {
            return this.columnType === ListType.milestone;
        },
        iterationTypeSelected () {
            return this.columnType === ListType.iteration;
        },
        statusTypeSelected () {
            return this.columnType === ListType.status;
        },
        hasLabelSelection () {
            return this.labelTypeSelected && this.selectedItem;
        },
        hasMilestoneSelection () {
            return this.milestoneTypeSelected && this.selectedItem;
        },
        hasIterationSelection () {
            return this.iterationTypeSelected && this.selectedItem;
        },
        hasAssigneeSelection () {
            return this.assigneeTypeSelected && this.selectedItem;
        },
        hasStatusSelection () {
            return this.statusTypeSelected && this.selectedItem;
        },
        columnForSelected () {
            if (!this.columnType || !this.selectedId) {
                return false;
            }
            return getListByTypeId(this.lists, this.columnType, this.selectedId);
        },
        loading () {
            return this[this.info.loadingPropertyName];
        },
        columnTypes () {
            const types = [
                {
                    value: ListType.label,
                    text: __('Label')
                }
            ];
            if (this.assigneeListsAvailable) {
                types.push({
                    value: ListType.assignee,
                    text: __('Assignee')
                });
            }
            if (this.milestoneListsAvailable) {
                types.push({
                    value: ListType.milestone,
                    text: __('Milestone')
                });
            }
            if (this.iterationListsAvailable) {
                types.push({
                    value: ListType.iteration,
                    text: __('Iteration')
                });
            }
            if (this.statusListsAvailable) {
                types.push({
                    value: ListType.status,
                    text: __('Status')
                });
            }
            return types;
        },
        searchLabel () {
            return this.showListTypeSelector ? this.$options.i18n.value : null;
        },
        showListTypeSelector () {
            return !this.isEpicBoard && this.columnTypes.length > 1;
        }
    },
    watch: {
        selectedId (val) {
            if (val) {
                this.selectedIdValid = true;
            }
        }
    },
    methods: {
        async createList ({ backlog, labelId, milestoneId, assigneeId, iterationId, statusId, position }) {
            try {
                await this.$apollo.mutate({
                    mutation: createListMutations[this.issuableType].mutation,
                    variables: {
                        labelId,
                        backlog,
                        milestoneId,
                        assigneeId,
                        iterationId,
                        statusId,
                        boardId: this.boardId,
                        position
                    },
                    update: (store, { data: { boardListCreate: { list } } })=>{
                        const sourceData = store.readQuery({
                            query: listsQuery[this.issuableType].query,
                            variables: this.listQueryVariables
                        });
                        const data = produce(sourceData, (draft)=>{
                            const lists = draft[this.boardType].board.lists.nodes;
                            if (position === null) {
                                lists.push({
                                    ...list,
                                    position: lists.length
                                });
                            } else {
                                const updatedLists = lists.map((l)=>{
                                    if (l.position >= position) {
                                        return {
                                            ...l,
                                            position: l.position + 1
                                        };
                                    }
                                    return l;
                                });
                                updatedLists.splice(position, 0, list);
                                draft[this.boardType].board.lists.nodes = updatedLists;
                            }
                        });
                        store.writeQuery({
                            query: listsQuery[this.issuableType].query,
                            variables: this.listQueryVariables,
                            data
                        });
                        this.$emit('highlight-list', list.id);
                    }
                });
            } catch (error) {
                setError({
                    error,
                    message: s__('Boards|An error occurred while creating the list. Please try again.')
                });
            }
        },
        async addList () {
            if (!this.selectedItem) {
                this.selectedIdValid = false;
                return;
            }
            if (this.columnForSelected) {
                const listId = this.columnForSelected.id;
                this.$emit('highlight-list', listId);
                return;
            }
            // eslint-disable-next-line @gitlab/require-i18n-strings
            await this.createList({
                [`${this.columnType}Id`]: this.selectedId,
                position: this.position
            });
            this.$emit('set-add-column-form-visibility', false);
        },
        onSearch: debounce(function debouncedSearch(searchTerm) {
            this.searchTerm = searchTerm;
        }, DEFAULT_DEBOUNCE_AND_THROTTLE_MS),
        showScopedLabels (label) {
            return this.scopedLabelsAvailable && isScopedLabel(label);
        },
        setColumnType (type) {
            this.columnType = type;
            this.selectedId = null;
            this.setSelectedItem(null);
        },
        setSelectedItem (selectedId) {
            this.selectedId = selectedId;
            const item = this.items.find(({ id })=>id === selectedId);
            if (!selectedId || !item) {
                this.selectedItem = null;
            } else {
                this.selectedItem = {
                    ...item
                };
            }
        },
        onHide () {
            this.searchValue = '';
            this.$emit('filter-items', '');
            this.$emit('hide');
        },
        getIterationPeriod
    }
};
